import React, { useState, useEffect } from 'react';
import { Snowflake, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

function KolikoDanaDoZime() {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [winterDate, setWinterDate] = useState(new Date());
  
  // Calculate winter start date (December 21st of current year)
  useEffect(() => {
    const now = new Date();
    const year = now.getFullYear();
    const winterStart = new Date(year, 11, 21); // December 21st (month is 0-indexed)
    
    // If winter has already started this year, set to next year
    if (now > winterStart) {
      winterStart.setFullYear(year + 1);
    }
    
    setWinterDate(winterStart);
    setCurrentYear(now.getFullYear());
  }, []);
  
  // Update countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const difference = winterDate.getTime() - now.getTime();
      
      if (difference <= 0) {
        clearInterval(timer);
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        });
        return;
      }
      
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      
      setTimeLeft({ days, hours, minutes, seconds });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [winterDate]);
  
  // Format number with leading zero
  const formatNumber = (num: number): string => {
    return num.toString().padStart(2, '0');
  };
  
  // Calculate percentage of time passed until winter
  const calculateProgress = (): number => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    // Start from either last winter or beginning of year
    const startDate = new Date(currentYear, 0, 1); // January 1st
    const lastWinter = new Date(currentYear, 11, 21);
    
    if (now > lastWinter) {
      startDate.setTime(lastWinter.getTime());
    }
    
    const totalTime = winterDate.getTime() - startDate.getTime();
    const elapsedTime = now.getTime() - startDate.getTime();
    
    return Math.min(100, Math.max(0, (elapsedTime / totalTime) * 100));
  };
  
  // Get season information
  const getCurrentSeason = (): string => {
    const now = new Date();
    const month = now.getMonth();
    
    if (month >= 2 && month < 5) return 'proljeće';
    if (month >= 5 && month < 8) return 'ljeto';
    if (month >= 8 && month < 11) return 'jesen';
    return 'zima';
  };
  
  const getSeasonEmoji = (season: string): string => {
    switch (season) {
      case 'proljeće': return '🌱';
      case 'ljeto': return '☀️';
      case 'jesen': return '🍂';
      case 'zima': return '❄️';
      default: return '';
    }
  };
  
  const season = getCurrentSeason();
  const progress = calculateProgress();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Koliko Dana Do Zime",
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Precizno odbrojavanje do početka zime. Saznajte točno koliko dana, sati, minuta i sekundi je ostalo do zime.",
    "url": "https://online-kalkulator.com/online-stoperica/koliko-dana-do-zime",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Precizno odbrojavanje do zime",
      "Prikaz dana, sati, minuta i sekundi",
      "Vizualni prikaz napretka",
      "Informacije o godišnjim dobima",
      "Automatsko ažuriranje"
    ]
  };

  return (
    <>
      <SEO 
        title="Koliko Dana Do Zime | Odbrojavanje Do Zime"
        description="Precizno odbrojavanje do početka zime. Saznajte točno koliko dana, sati, minuta i sekundi je ostalo do zime."
        canonicalUrl="/online-stoperica/koliko-dana-do-zime"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Snowflake className="w-8 h-8 text-blue-500" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Koliko Dana Do Zime</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Countdown Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Season Info */}
              <div className="text-center mb-4">
                <div className="inline-block px-4 py-2 bg-blue-50 rounded-full">
                  <span className="text-lg font-medium text-blue-700">
                    Trenutno godišnje doba: <span className="font-bold">{season} {getSeasonEmoji(season)}</span>
                  </span>
                </div>
              </div>
              
              {/* Target Date */}
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-gray-600 mb-2">
                  <Calendar className="w-5 h-5" />
                  <span>Zima počinje: {winterDate.toLocaleDateString('hr-HR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-4 mb-6">
                <div 
                  className="h-full bg-gradient-to-r from-orange-500 via-blue-300 to-blue-500 rounded-full transition-all duration-1000"
                  style={{ width: `${progress}%` }}
                />
              </div>

              {/* Countdown Display */}
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-4xl font-bold text-blue-600 font-mono">
                    {timeLeft.days}
                  </div>
                  <div className="text-gray-600 text-sm mt-1">Dana</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-4xl font-bold text-blue-600 font-mono">
                    {formatNumber(timeLeft.hours)}
                  </div>
                  <div className="text-gray-600 text-sm mt-1">Sati</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-4xl font-bold text-blue-600 font-mono">
                    {formatNumber(timeLeft.minutes)}
                  </div>
                  <div className="text-gray-600 text-sm mt-1">Minuta</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-4xl font-bold text-blue-600 font-mono">
                    {formatNumber(timeLeft.seconds)}
                  </div>
                  <div className="text-gray-600 text-sm mt-1">Sekundi</div>
                </div>
              </div>
              
              {/* Winter Message */}
              {season === 'zima' && (
                <div className="text-center py-3 px-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-center gap-2">
                    <Snowflake className="w-5 h-5 text-blue-500" />
                    <p className="text-lg font-medium text-blue-700">Zima je već počela! Uživajte u snijegu i toplim napitcima!</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Odbrojavanju Do Zime</h2>
                <p className="text-gray-600 leading-relaxed">
                  Odbrojavanje do zime je zabavan način da pratite koliko je vremena preostalo do
                  službenog početka zime. Zima astronomski počinje na zimski solsticij, koji pada
                  oko 21. prosinca na sjevernoj hemisferi. Ovaj datum označava najkraći dan i najdužu noć
                  u godini, nakon čega dani počinju postupno postajati duži.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Godišnja Doba</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🌱</span>
                      <h3 className="text-lg font-medium text-green-700">Proljeće</h3>
                    </div>
                    <p className="text-gray-600">
                      Počinje oko 20. ožujka i traje do 21. lipnja. Karakteriziraju ga umjerene temperature
                      i buđenje prirode nakon zime.
                    </p>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">☀️</span>
                      <h3 className="text-lg font-medium text-yellow-700">Ljeto</h3>
                    </div>
                    <p className="text-gray-600">
                      Počinje oko 21. lipnja i traje do 23. rujna. Najduži dani u godini, visoke
                      temperature i vrijeme za odmor i uživanje na otvorenom.
                    </p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🍂</span>
                      <h3 className="text-lg font-medium text-orange-700">Jesen</h3>
                    </div>
                    <p className="text-gray-600">
                      Počinje oko 23. rujna i traje do 21. prosinca. Karakteriziraju je promjene boja
                      lišća, umjerene temperature i berba plodova.
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">❄️</span>
                      <h3 className="text-lg font-medium text-blue-700">Zima</h3>
                    </div>
                    <p className="text-gray-600">
                      Počinje oko 21. prosinca i traje do 20. ožujka. Najkraći dani u godini, niske
                      temperature i često snijeg u kontinentalnim područjima.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Zanimljivosti o Zimi</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Zimski solsticij je najkraći dan u godini na sjevernoj hemisferi</li>
                  <li>Na Antarktici, zimski dan može trajati i do 24 sata</li>
                  <li>Snježne pahuljice uvijek imaju šest strana</li>
                  <li>Zimi je Zemlja zapravo bliže Suncu nego ljeti (u sjevernoj hemisferi)</li>
                  <li>Mnoge životinje hiberniraju tijekom zime kako bi sačuvale energiju</li>
                  <li>Najniža ikad zabilježena temperatura na Zemlji je -89.2°C na Antarktici</li>
                  <li>Zimski sportovi poput skijanja i klizanja postali su popularni u 19. stoljeću</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti Odbrojavanje</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Odbrojavanje se automatski ažurira svake sekunde</li>
                  <li>Prikazuje točan broj dana, sati, minuta i sekundi do početka zime</li>
                  <li>Traka napretka vizualno prikazuje koliko je vremena prošlo od zadnje zime ili početka godine</li>
                  <li>Ako je trenutno zima, odbrojavanje će pokazivati vrijeme do sljedeće zime</li>
                  <li>Stranica se automatski ažurira svake godine za novo odbrojavanje</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Planiranje Zimskih Aktivnosti</h2>
                <div className="space-y-3">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-1">Zimski sportovi</h3>
                    <p className="text-gray-600 text-sm">
                      Iskoristite odbrojavanje za planiranje zimskih aktivnosti poput skijanja, snowboardinga, 
                      klizanja ili sanjkanja.
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-1">Blagdanske pripreme</h3>
                    <p className="text-gray-600 text-sm">
                      Planirajte blagdanske dekoracije, kupovinu poklona i organizaciju proslava.
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-1">Zimski odmor</h3>
                    <p className="text-gray-600 text-sm">
                      Rezervirajte zimski odmor u planinama ili toplim destinacijama na vrijeme.
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-1">Priprema doma</h3>
                    <p className="text-gray-600 text-sm">
                      Pripremite svoj dom za zimu - izolacija, grijanje, zimske zalihe i oprema.
                    </p>
                  </div>
                </div>
              </section>

              {/* Interlinking Section */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Ostala Odbrojavanja</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Link to="/online-stoperica/koliko-dana-do-proljeca" className="p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🌱</span>
                      <h3 className="text-lg font-medium text-green-700">Koliko Dana Do Proljeća</h3>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Saznajte koliko je dana ostalo do početka proljeća.
                    </p>
                  </Link>
                  <Link to="/online-stoperica/koliko-dana-do-ljeta" className="p-4 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">☀️</span>
                      <h3 className="text-lg font-medium text-yellow-700">Koliko Dana Do Ljeta</h3>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Saznajte koliko je dana ostalo do početka ljeta.
                    </p>
                  </Link>
                  <Link to="/online-stoperica/koliko-dana-do-jeseni" className="p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🍂</span>
                      <h3 className="text-lg font-medium text-orange-700">Koliko Dana Do Jeseni</h3>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Saznajte koliko je dana ostalo do početka jeseni.
                    </p>
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KolikoDanaDoZime;